import React, { createContext, useContext, useState, useCallback } from 'react';
import locationService from '../services/location.service';

const LocationContext = createContext();

export const LocationProvider = ({ children }) => {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Don't fetch on mount - let components fetch when they need it
  // This prevents 401 errors before authentication is complete

  const fetchCountries = useCallback(async () => {
    try {
      const response = await locationService.getAllCountries();
      setCountries(response.data || []);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching countries:', error);
      return [];
    }
  }, []);

  const fetchStates = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      const response = await locationService.getAllStates(params);
      setStates(response.data || []);
      setError(null);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching states:', error);
      // Don't set error for 401 - it means user isn't authenticated yet
      if (error.statusCode !== 401) {
        setError('Failed to load states');
      }
      setStates([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getCitiesByState = async (stateId) => {
    try {
      if (!stateId) return [];
      const response = await locationService.getCitiesByState(stateId);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    }
  };

  const getStateById = (stateId) => {
    return states.find(state => state.id === parseInt(stateId));
  };

  return (
    <LocationContext.Provider value={{ 
      countries,
      states, 
      loading, 
      error,
      fetchCountries,
      fetchStates,
      getCitiesByState,
      getStateById,
      refreshStates: fetchStates
    }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocation = () => {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within LocationProvider');
  }
  return context;
};

export default LocationContext;
